import { useState } from "react";
import { MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateProspectList,
  useDeleteProspectList,
  useUpdateProspectList,
  type ProspectList,
} from "@/hooks/useProspects";
import { cn } from "@/lib/utils";

/**
 * Listas de leads frios (lotes de prospecção) com contagem, seleção, criar,
 * renomear, arquivar e excluir. A lista ativa vem do pai (`?list=`).
 */
export interface ProspectListNavProps {
  lists: ProspectList[];
  activeId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}

export function ProspectListNav({ lists, activeId, onSelect, className }: ProspectListNavProps) {
  const [dialog, setDialog] = useState<{ mode: "create" | "rename"; list?: ProspectList } | null>(
    null,
  );
  const [name, setName] = useState("");
  const [toDelete, setToDelete] = useState<ProspectList | null>(null);
  const create = useCreateProspectList();
  const update = useUpdateProspectList();
  const remove = useDeleteProspectList();

  const openDialog = (mode: "create" | "rename", list?: ProspectList) => {
    setName(list?.name ?? "");
    setDialog({ mode, list });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !dialog) return;
    if (dialog.mode === "create") {
      const created = await create.mutateAsync({ name });
      onSelect(created.id);
    } else if (dialog.list) {
      await update.mutateAsync({ id: dialog.list.id, name });
    }
    setDialog(null);
  };

  return (
    <nav aria-label="Listas de leads frios" className={cn("flex flex-col gap-1", className)}>
      <div className="px-2.5 pb-1 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
        Listas
      </div>
      {lists.map((l) => (
        <div
          key={l.id}
          className={cn(
            "group flex items-center rounded-[9px]",
            l.id === activeId ? "bg-accent text-primary" : "hover:bg-muted",
          )}
        >
          <button
            type="button"
            aria-current={l.id === activeId ? "page" : undefined}
            onClick={() => onSelect(l.id)}
            className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2.5 py-2 text-left text-[13px]"
          >
            <span className={cn("truncate", l.id === activeId && "font-semibold")}>{l.name}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground">{l.total}</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Ações da lista ${l.name}`}
                className="mr-1 rounded-md p-1 text-muted-foreground opacity-60 hover:bg-background hover:opacity-100"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openDialog("rename", l)}>Renomear</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => update.mutate({ id: l.id, archived: true })}>
                Arquivar
              </DropdownMenuItem>
              <DropdownMenuItem className="text-bad" onSelect={() => setToDelete(l)}>
                Excluir…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        className="mt-1 justify-start border-dashed text-[13px] text-muted-foreground"
        onClick={() => openDialog("create")}
      >
        <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Nova lista
      </Button>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-[420px]">
          <form onSubmit={submit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>
                {dialog?.mode === "rename" ? "Renomear lista" : "Nova lista"}
              </DialogTitle>
              <DialogDescription>
                Um lote ou fonte de prospecção (ex.: “Apify — Três Marias”).
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="prospect-list-name">Nome</Label>
              <Input
                id="prospect-list-name"
                autoFocus
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!name.trim() || create.isPending || update.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Apaga a lista, as colunas e os {toDelete?.total ?? 0} leads frios dela. Leads já
              movidos para o funil continuam no funil. Não dá para desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bad text-bad-foreground hover:bg-bad/90"
              onClick={() => toDelete && remove.mutate(toDelete.id)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </nav>
  );
}
